import React from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Authenticated, Unauthenticated } from 'convex/react';
import { useQuery } from '../lib/convex';
import { api } from '../lib/convex';
import { SignOut } from '../components/auth/TeacherAuth';

const HomePage: React.FC = () => {
  const currentUser = useQuery(api.users.currentUser);

  return (
    <div className="container max-w-6xl mx-auto px-4 py-8">
      <div className="text-center">
        <div className="mb-8 inline-block">
          <h1 className="text-6xl font-bold font-heading mb-2 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent drop-shadow-lg">
            Interactive Classroom Quiz
          </h1>
          <div className="flex items-center justify-center gap-2 text-2xl">
            <span className="animate-bounce">🍂</span>
            <span className="text-accent font-semibold">Fall Edition</span>
            <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>🍁</span>
          </div>
          
          <Authenticated>
            {currentUser && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <span className="text-lg">Hello: <strong>{currentUser.name}</strong></span>
                <SignOut />
              </div>
            )}
          </Authenticated>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
          <Card className="text-center hover:shadow-2xl transition-shadow duration-300 border-4 border-primary/30 bg-gradient-to-br from-white to-orange-50">
            <CardHeader className="space-y-3">
              <div className="text-5xl mb-2">🍎</div>
              <CardTitle className="text-3xl text-primary">I'm a Teacher</CardTitle>
              <CardDescription className="text-base font-semibold">Create and manage interactive quizzes</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-6 text-foreground/80">Create custom quizzes, run interactive sessions, and track student performance.</p>
              <Link to="/teacher">
                <Button size="lg" className="w-full">
                  🍎 Teacher Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="text-center hover:shadow-2xl transition-shadow duration-300 border-4 border-secondary/30 bg-gradient-to-br from-white to-yellow-50">
            <CardHeader className="space-y-3">
              <div className="text-5xl mb-2">📚</div>
              <CardTitle className="text-3xl text-accent">I'm a Student</CardTitle>
              <CardDescription className="text-base font-semibold">Join a quiz session</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-6 text-foreground/80">Enter a session code to join a quiz and compete with your classmates.</p>
              <Link to="/join" search={{ code: undefined }}>
                <Button size="lg" className="w-full">
                  🚀 Join a Quiz
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 max-w-3xl mx-auto">
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
            <CardHeader>
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-3xl">🎓</span>
                <h2 className="text-3xl font-bold font-heading text-accent">About This App</h2>
                <span className="text-3xl">✨</span>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-foreground/80 leading-relaxed">
                This interactive quiz platform allows teachers to create engaging real-time quizzes for their students.
                Students can join via their devices and answer questions while competing for the highest score.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HomePage;